import * as amplitude from '@amplitude/analytics-browser';
import { useAuthStore } from '@stores/auth';

import { TrackingError } from '@utils/errors';
import { defineNuxtPlugin } from '#imports';
import type { AmplitudeEventName } from '@utils/trackingEvents';
import type { PossibleCaptureExceptionFn } from '@composables/useErrorLogger';

const analyticsTrack =
    (captureExceptionFn: PossibleCaptureExceptionFn) =>
    async <T extends AmplitudeEventName>(
        eventName: T | amplitude.Types.BaseEvent,
        data: AmplitudeEventsData[T],
        eventOptions?: amplitude.Types.EventOptions | undefined
    ) => {
        const response = amplitude.track(
            eventName,
            { ...data, product: 'attendee-space' },
            eventOptions
        );
        const { code, message } = await response.promise;
        if (code.toString().startsWith('4')) {
            captureExceptionFn(new TrackingError('Amplitude tracking error'), {
                extra: {
                    code,
                    message,
                },
            });
        }
        return response;
    };

const missingDataHandler = () => {
    console.warn(
        'Amplitude Analytics is not configured properly. Please check amplitudeToken and amplitudeUrl in config.'
    );
};

const analyticsTrackFactory = (
    hasKey: boolean,
    captureExceptionFn: PossibleCaptureExceptionFn
) => {
    return hasKey ? analyticsTrack(captureExceptionFn) : missingDataHandler;
};

export type AnalyticsTrackFn = ReturnType<typeof analyticsTrackFactory>;

const analyticsSetUserIdFactory = (hasKey: boolean) => {
    return hasKey ? amplitude.setUserId : missingDataHandler;
};

const getHasValidData = (key: string, url: string) => {
    return !!(key && url);
};

export default defineNuxtPlugin(({ $config }) => {
    const authStore = useAuthStore();
    const { errorLoggerCaptureException } = useErrorLogger();

    const { amplitudeToken, amplitudeUrl } = $config.public;
    const hasValidData = getHasValidData(amplitudeToken, amplitudeUrl);

    if (hasValidData) {
        amplitude.init(amplitudeToken, {
            serverUrl: amplitudeUrl,
            serverZone: 'EU',
            autocapture: false,
            userId: authStore.user?.id,
        });
    }

    const analyticsTrack = analyticsTrackFactory(
        hasValidData,
        errorLoggerCaptureException
    );

    const analyticsSetUserId = analyticsSetUserIdFactory(hasValidData);

    return {
        provide: {
            analyticsTrack,
            analyticsSetUserId,
        },
    };
});
